import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { OnChangeFn, PaginationState } from '@tanstack/react-table';
import { Button, Tabs, Tab, useDisclosure } from '@heroui/react';
import {
  useGetTermsQuery,
  useGetTermModulesQuery,
  useGetTermStudentsQuery,
  useDeleteModuleMutation,
  Module,
  TermStudent,
} from '@/entities/Courses';
import { useDeleteStudentMutation, useToggleUserBlockMutation } from '@/entities/Students';
import { Container } from '@/shared/ui/Container';
import { CourseBreadcrumbs } from '@/shared/ui/CourseBreadcrumbs';
import { CustomInput, SelectSort } from '@/shared/ui/Fields';
import { ContainerTable } from '@/shared/ui/AppTable';
import { Svg } from '@/shared/ui/Svg';
import { MessageModal } from '@/shared/ui/Modal';
import { useDebounce } from '@/shared/lib/hooks/useDebounce';
import { useToast } from '@/shared/lib/hooks/useToast';
import { useAdminAccess } from '@/shared/lib/adminAccess';
import { searchInputClassName } from '@/shared/config/searchInputClassName';
import SearchIcon from '@/shared/assets/icons/search.svg';
import FilterIcon from '@/shared/assets/icons/filter.svg';
import { SectionLoader } from '@/shared/ui/Loader';
import { routerPath } from '@/shared/config/router';
import { Divider } from '@/shared/ui/Divider';
import { modulesColumns } from '../../config/modulesColumns';
import { studentsColumns } from '../../config/studentsColumns';
import { modulesSortOptions, studentsSortOptions } from '../../config/sortOptions';
import { AddModuleModal } from '../components/AddModuleModal';
import { EditModuleModal } from '../components/EditModuleModal';
import { TermStudentsSelectedActions } from '../components/TermStudentsSelectedActions';
import { TransferToNextTermModal } from '../components/TransferToNextTermModal';
import styles from './TermDetailView.module.scss';

interface TermDetailViewProps {
  courseId: number;
  intakeId: number;
  groupNumber: number;
  termId: number;
}

export const TermDetailView = ({
  courseId,
  intakeId,
  groupNumber,
  termId,
}: TermDetailViewProps) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<string>('modules');
  const [modulesPagination, setModulesPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [studentsPagination, setStudentsPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [search, setSearch] = useState('');
  const trimmedSearch = search.trim();
  const debouncedSearch = useDebounce(trimmedSearch, 500);
  const [modulesSort, setModulesSort] = useState<string>('default');
  const [studentsSort, setStudentsSort] = useState<string>('default');
  const [isModuleModalOpen, setIsModuleModalOpen] = useState(false);
  const [isEditModuleModalOpen, setIsEditModuleModalOpen] = useState(false);
  const [isTransferModalOpen, setIsTransferModalOpen] = useState(false);
  const [editingModule, setEditingModule] = useState<Module | null>(null);
  const [moduleToDelete, setModuleToDelete] = useState<Module | null>(null);
  const [selectedStudents, setSelectedStudents] = useState<TermStudent[]>([]);
  const [blockStudentTarget, setBlockStudentTarget] = useState<TermStudent | null>(null);
  const [deleteStudentTarget, setDeleteStudentTarget] = useState<TermStudent | null>(null);
  const prevSearchRef = useRef(debouncedSearch);

  const {
    isOpen: isDeleteModuleModalOpen,
    onOpen: onDeleteModuleModalOpen,
    onOpenChange: onDeleteModuleModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isBlockStudentModalOpen,
    onOpen: onBlockStudentModalOpen,
    onOpenChange: onBlockStudentModalOpenChange,
  } = useDisclosure();
  const {
    isOpen: isDeleteStudentModalOpen,
    onOpen: onDeleteStudentModalOpen,
    onOpenChange: onDeleteStudentModalOpenChange,
  } = useDisclosure();

  const { toast } = useToast();
  const { canEntity } = useAdminAccess();
  const canModuleCreate = canEntity('module', 'create');
  const canModuleUpdate = canEntity('module', 'update');
  const canModuleDelete = canEntity('module', 'delete');
  // Перевод на следующий терм бэкенд проверяет как term.update
  const canTermTransfer = canEntity('term', 'update');
  const canStudentUpdate = canEntity('student', 'update');
  const canStudentDelete = canEntity('student', 'delete');
  const { data: termsData, isLoading: isTermsLoading } = useGetTermsQuery({ intakeId });
  const [deleteModule, { isLoading: isDeletingModule }] = useDeleteModuleMutation();
  const [toggleUserBlock, { isLoading: isBlockingStudent }] = useToggleUserBlockMutation();
  const [deleteStudent, { isLoading: isDeletingStudent }] = useDeleteStudentMutation();

  const term = useMemo(
    () => termsData?.data?.terms.find((t) => t.id === termId),
    [termsData?.data?.terms, termId],
  );
  const intakeTermId = term?.intakeTermId;
  const intakeIdFromData = termsData?.data?.intake?.id;

  const groupId = useMemo(() => {
    const group = termsData?.data?.groups.find((g) => g.number === groupNumber);
    return group?.id;
  }, [termsData?.data?.groups, groupNumber]);

  const { data: modulesData, isLoading: isModulesLoading } = useGetTermModulesQuery(
    {
      groupNumber,
      intakeTermId: intakeTermId || 0,
      page: modulesPagination.pageIndex + 1,
      pageSize: modulesPagination.pageSize,
      search: debouncedSearch || undefined,
      sort: modulesSort !== 'default' ? modulesSort : undefined,
    },
    { skip: !intakeTermId },
  );
  const {
    data: studentsData,
    isLoading: isStudentsLoading,
    refetch: refetchStudents,
  } = useGetTermStudentsQuery(
    {
      groupNumber,
      intakeTermId: intakeTermId || 0,
      page: studentsPagination.pageIndex + 1,
      pageSize: studentsPagination.pageSize,
      search: debouncedSearch || undefined,
      sort: studentsSort !== 'default' ? studentsSort : undefined,
    },
    { skip: activeTab !== 'students' || !intakeTermId },
  );

  useEffect(() => {
    if (prevSearchRef.current !== debouncedSearch) {
      prevSearchRef.current = debouncedSearch;
      setModulesPagination((prev) => ({ ...prev, pageIndex: 0 }));
      setStudentsPagination((prev) => ({ ...prev, pageIndex: 0 }));
    }
  }, [debouncedSearch]);

  const modules = useMemo(() => modulesData?.data?.modules || [], [modulesData?.data?.modules]);

  const students = useMemo(
    () => studentsData?.data?.students || [],
    [studentsData?.data?.students],
  );
  const studentsPaginationMeta = studentsData?.data?.pagination;

  const firstModule = modules[0];

  const handleExport = () => {
    // TODO: Implement export
  };

  const handleAddModule = () => {
    setIsModuleModalOpen(true);
  };

  const handleModuleModalSuccess = () => {
    setIsModuleModalOpen(false);
  };

  const handleEditModule = (module: Module) => {
    setEditingModule(module);
    setIsEditModuleModalOpen(true);
  };

  const handleModuleRowClick = (module: Module) => {
    navigate(
      routerPath.moduleDetail
        .replace(':courseId', courseId.toString())
        .replace(':intakeId', intakeId.toString())
        .replace(':groupNumber', groupNumber.toString())
        .replace(':termId', termId.toString())
        .replace(':moduleId', module.intakeModuleId.toString()),
    );
  };

  const handleEditModuleModalSuccess = () => {
    setIsEditModuleModalOpen(false);
    setEditingModule(null);
  };

  const handleDeleteModuleClick = (module: Module) => {
    setModuleToDelete(module);
    onDeleteModuleModalOpen();
  };

  const handleDeleteModule = async () => {
    if (!moduleToDelete) return;
    try {
      await deleteModule(moduleToDelete.id).unwrap();
      toast({
        title: 'Success',
        description: 'Module deleted successfully',
        color: 'success',
      });
      setModuleToDelete(null);
      onDeleteModuleModalOpenChange();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete module',
        color: 'danger',
      });
      onDeleteModuleModalOpenChange();
    }
  };

  const handleBlockStudentClick = (student: TermStudent) => {
    setBlockStudentTarget(student);
    onBlockStudentModalOpen();
  };

  const handleConfirmBlockStudent = async () => {
    if (!blockStudentTarget) return;
    const nextBlocked = !blockStudentTarget.blocked;
    try {
      await toggleUserBlock({ userId: blockStudentTarget.id }).unwrap();
      toast({
        title: 'Success',
        description: `Student ${nextBlocked ? 'blocked' : 'unblocked'} successfully`,
        color: 'success',
      });
      setBlockStudentTarget(null);
      onBlockStudentModalOpenChange();
      refetchStudents();
    } catch (error: any) {
      toast({
        title: 'Error',
        description:
          error?.data?.message || `Failed to ${nextBlocked ? 'block' : 'unblock'} student`,
        color: 'danger',
      });
      onBlockStudentModalOpenChange();
    }
  };

  const handleDeleteStudentClick = (student: TermStudent) => {
    setDeleteStudentTarget(student);
    onDeleteStudentModalOpen();
  };

  const handleConfirmDeleteStudent = async () => {
    if (!deleteStudentTarget) return;
    try {
      await deleteStudent({
        enrollmentId: deleteStudentTarget.id,
      }).unwrap();
      toast({
        title: 'Success',
        description: 'Student deleted successfully',
        color: 'success',
      });
      setDeleteStudentTarget(null);
      onDeleteStudentModalOpenChange();
      refetchStudents();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error?.data?.message || 'Failed to delete student',
        color: 'danger',
      });
      onDeleteStudentModalOpenChange();
    }
  };

  const handleFilterModules = () => {
    // TODO: Implement filter modules
  };

  const handleCheckAttendance = () => {
    if (!firstModule) return;

    navigate(
      routerPath.attendance
        .replace(':courseId', courseId.toString())
        .replace(':intakeId', intakeId.toString())
        .replace(':groupNumber', groupNumber.toString())
        .replace(':termId', termId.toString())
        .replace(':moduleId', firstModule.intakeModuleId.toString()),
    );
  };

  const handleTransferToNextTerm = () => {
    setIsTransferModalOpen(true);
  };

  const handleTransferModalSuccess = () => {
    setIsTransferModalOpen(false);
  };

  if (isTermsLoading) {
    return <SectionLoader />;
  }

  if (!term) {
    return null;
  }

  const courseTitle = termsData?.data?.course?.title || '';
  const intakeTitle = termsData?.data?.intake?.title || '';
  const intakeGroupLabel = intakeTitle
    ? `${intakeTitle} - Group ${groupNumber}`
    : `Group ${groupNumber}`;
  const groupDetailPath = routerPath.groupDetail
    .replace(':courseId', String(courseId))
    .replace(':intakeId', String(intakeId))
    .replace(':groupNumber', String(groupNumber));

  const breadcrumbItems = [
    { label: 'Courses', href: routerPath.courses },
    {
      label: courseTitle || 'Course',
      href: routerPath.courseDetail.replace(':id', String(courseId)),
    },
    { label: intakeGroupLabel, href: groupDetailPath },
    { label: `Term ${term.number}` },
  ];

  return (
    <div className={styles.root}>
      <Container className="flex flex-col gap-6">
        <CourseBreadcrumbs items={breadcrumbItems} />

        <div className="inline-flex gap-x-2 items-end">
          <h2 className="text-[32px] font-medium">
            Group {groupNumber} - Term {term.number}
          </h2>
          <p className="text-grey-secondary leading-[220%]">
            (found{' '}
            {activeTab === 'modules'
              ? modules.length
              : studentsPaginationMeta?.total || students.length}{' '}
            {activeTab === 'modules' ? 'modules' : 'students'})
          </p>
        </div>
        <div className="flex items-center justify-between">
          <div className="w-full max-w-[530px]">
            <CustomInput
              className={searchInputClassName}
              startContent={<Svg Icon={SearchIcon} />}
              placeholder="Search"
              value={search}
              onValueChange={setSearch}
            />
          </div>
          <div className="flex items-center gap-4">
            {activeTab === 'modules' && canModuleCreate && (
              <Button
                className="bg-[#211D55] text-white font-medium font-base rounded-lg"
                onPress={handleAddModule}
              >
                Add new module
              </Button>
            )}
            <div className="flex flex-col gap-4">
              <div className="flex gap-4">
                <Button
                  variant="bordered"
                  className="border-[#211D55] bg-white font-medium font-base text-[#211D55] min-w-[152px] rounded-lg"
                  onPress={handleExport}
                >
                  Export
                </Button>
                {activeTab === 'students' && (
                  <Button
                    variant="bordered"
                    className="border-[#211D55] bg-white font-medium font-base text-[#211D55] rounded-lg"
                    onPress={handleCheckAttendance}
                    isDisabled={!firstModule}
                  >
                    Check attendance
                  </Button>
                )}
              </div>
              {activeTab === 'students' && canTermTransfer && (
                <Button
                  variant="bordered"
                  className="border-[#211D55] bg-white font-medium font-base text-[#211D55] rounded-lg"
                  onPress={handleTransferToNextTerm}
                >
                  Transfer to the next term
                </Button>
              )}
            </div>
          </div>
        </div>

        <Divider />

        <div className="flex flex-col gap-4">
          <Tabs
            aria-label="Term detail tabs"
            selectedKey={activeTab}
            onSelectionChange={(key) => setActiveTab(key as string)}
            classNames={{
              tabList: 'gap-0 bg-transparent flex-shrink-0',
              tab: 'px-6 py-3 bg-transparent text-grey-secondary data-[selected=true]:bg-[#1E1E1E] rounded-lg',
              tabContent: 'text-grey-secondary data-[selected=true]:!text-[#FFFFFF]',
              panel: 'pt-0',
            }}
          >
            <Tab key="modules" title="Modules">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <Button isIconOnly className="bg-brand rounded" onPress={handleFilterModules}>
                    <Svg Icon={FilterIcon} className="text-white" />
                  </Button>
                  <SelectSort
                    sections={modulesSortOptions}
                    defaultValue={modulesSort}
                    onSelectionChange={(keys) => {
                      const newValue = new Set(keys).values().next().value || 'default';
                      setModulesSort(newValue as string);
                    }}
                  />
                </div>
                {isModulesLoading ? (
                  <SectionLoader />
                ) : (
                  <ContainerTable<Module>
                    data={modules}
                    columns={modulesColumns()}
                    pagination={modulesPagination}
                    setPagination={setModulesPagination as OnChangeFn<PaginationState>}
                    hideTopActions={true}
                    tableMeta={{
                      onEdit: canModuleUpdate ? handleEditModule : undefined,
                      onDelete: canModuleDelete ? handleDeleteModuleClick : undefined,
                    }}
                    onRowClick={handleModuleRowClick}
                  />
                )}
              </div>
            </Tab>
            <Tab key="students" title="Students">
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Button isIconOnly className="bg-brand rounded" onPress={handleFilterModules}>
                      <Svg Icon={FilterIcon} className="text-white" />
                    </Button>
                    {selectedStudents.length > 0 && (
                      <TermStudentsSelectedActions
                        selectedRows={selectedStudents}
                        onRemoveSelected={() => setSelectedStudents([])}
                        onBlockSelected={canStudentUpdate ? handleCheckAttendance : undefined}
                        onDeleteSelected={canStudentDelete ? handleTransferToNextTerm : undefined}
                      />
                    )}
                  </div>
                  <SelectSort
                    sections={studentsSortOptions}
                    defaultValue={studentsSort}
                    onSelectionChange={(keys) => {
                      const newValue = new Set(keys).values().next().value || 'default';
                      setStudentsSort(newValue as string);
                    }}
                  />
                </div>
                {isStudentsLoading ? (
                  <SectionLoader />
                ) : (
                  <ContainerTable<TermStudent>
                    data={students}
                    columns={studentsColumns()}
                    pagination={studentsPagination}
                    setPagination={setStudentsPagination as OnChangeFn<PaginationState>}
                    hideTopActions={true}
                    onSelectedItemsChange={setSelectedStudents}
                    tableMeta={{
                      onBlock: canStudentUpdate ? handleBlockStudentClick : undefined,
                      onDelete: canStudentDelete ? handleDeleteStudentClick : undefined,
                    }}
                    meta={
                      studentsPaginationMeta
                        ? {
                            page: studentsPaginationMeta.page,
                            pageSize: studentsPaginationMeta.pageSize,
                            total: studentsPaginationMeta.total,
                            pageCount: studentsPaginationMeta.pageCount,
                          }
                        : undefined
                    }
                  />
                )}
              </div>
            </Tab>
          </Tabs>
        </div>
      </Container>

      {intakeTermId && (
        <>
          <AddModuleModal
            isOpen={isModuleModalOpen}
            onOpenChange={setIsModuleModalOpen}
            courseId={courseId}
            intakeTermId={intakeTermId}
            groupNumber={groupNumber}
            onSuccess={handleModuleModalSuccess}
          />
          <EditModuleModal
            isOpen={isEditModuleModalOpen}
            onOpenChange={setIsEditModuleModalOpen}
            courseId={courseId}
            module={editingModule}
            intakeId={intakeIdFromData || intakeId}
            termNumber={term?.number || 0}
            groupId={groupId || 0}
            onSuccess={handleEditModuleModalSuccess}
          />
          <TransferToNextTermModal
            isOpen={isTransferModalOpen}
            onOpenChange={setIsTransferModalOpen}
            intakeId={intakeIdFromData || intakeId}
            groupId={groupId}
            fromTermNumber={term?.number || 0}
            onSuccess={handleTransferModalSuccess}
          />
          <MessageModal
            title="Delete Module"
            text={
              moduleToDelete
                ? `Are you sure you want to delete the module "${moduleToDelete.title}"? This action cannot be undone. All related data (lessons, intake modules, module teachers, etc.) will be permanently deleted. Allowed only when there are no assignment submissions linked to lessons in this module.`
                : ''
            }
            confirmText="Delete"
            isOpen={isDeleteModuleModalOpen}
            onOpenChange={() => {
              setModuleToDelete(null);
              onDeleteModuleModalOpenChange();
            }}
            variant="red"
            toggleVisibility={handleDeleteModule}
            isLoading={isDeletingModule}
          />
          <MessageModal
            title={blockStudentTarget?.blocked ? 'Unblock Student' : 'Block Student'}
            text={
              blockStudentTarget
                ? `Are you sure you want to ${blockStudentTarget.blocked ? 'unblock' : 'block'} ${
                    `${blockStudentTarget.firstName || ''} ${blockStudentTarget.lastName || ''}`.trim() ||
                    blockStudentTarget.username
                  }?`
                : ''
            }
            confirmText={blockStudentTarget?.blocked ? 'Unblock' : 'Block'}
            isOpen={isBlockStudentModalOpen}
            onOpenChange={() => {
              setBlockStudentTarget(null);
              onBlockStudentModalOpenChange();
            }}
            variant="red"
            toggleVisibility={handleConfirmBlockStudent}
            isLoading={isBlockingStudent}
          />
          <MessageModal
            title="Delete Student"
            text={
              deleteStudentTarget
                ? `Are you sure you want to delete ${
                    `${deleteStudentTarget.firstName || ''} ${deleteStudentTarget.lastName || ''}`.trim() ||
                    deleteStudentTarget.username
                  }? This action cannot be undone.`
                : ''
            }
            confirmText="Delete"
            isOpen={isDeleteStudentModalOpen}
            onOpenChange={() => {
              setDeleteStudentTarget(null);
              onDeleteStudentModalOpenChange();
            }}
            variant="red"
            toggleVisibility={handleConfirmDeleteStudent}
            isLoading={isDeletingStudent}
          />
        </>
      )}
    </div>
  );
};
